/* eslint-disable no-restricted-syntax */
import { Injectable } from '@angular/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

import { Observable, of } from 'rxjs';
import { DeliveryAsset } from '../models';
import { DirectorySize } from './directory-size.service';
import { ToastService } from './toast.service';

const serviceName = 'offline-file.service';

@Injectable({
  providedIn: 'root',
})
export class OfflineFileService {
  constructor(
    private dirSize: DirectorySize,
    private toastService: ToastService,
  ) {}

  async writeToImgFile(params): Promise<any> {
    const { file, field, deliveryKeyCode } = params;
    const fileName = `${field}.jpg`;
    await this.ensureFolderDirectory(deliveryKeyCode);
    const uuid = localStorage.getItem('uuid');
    const path = `${uuid}/${deliveryKeyCode}/${fileName}`;

    return Filesystem.writeFile({
      path,
      data: file,
      directory: Directory.Data,
    }).catch((err: any) => {
      this.toastService.showErrorInToast(serviceName, 'writeToImgFile.writeFile', err);
    });
  }

  async getFolderDirectory(deliveryKeyCode): Promise<string> {
    await this.ensureFolderDirectory(deliveryKeyCode);
    const uuid = localStorage.getItem('uuid');
    return `${uuid}/${deliveryKeyCode}/`;
  }

  async writeAudioFile(asset: DeliveryAsset, file: Blob): Promise<any> {
    const fileName = this.getAudioFileName(asset);
    await this.ensureFolderDirectory(asset.deliveryKeyCode);
    const uuid = localStorage.getItem('uuid');
    const path = `${uuid}/${asset.deliveryKeyCode}/${fileName}`;

    const base64 = await this.blobToBase64(file);
    return Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Data,
    }).catch((err: any) => {
      this.toastService.showErrorInToast(serviceName, 'writeAudioFile.writeFile', err);
    });
  }

  async getAudioLocalUrl(asset: DeliveryAsset): Promise<string> {
    const uuid = localStorage.getItem('uuid');
    const path = `${uuid}/${asset.deliveryKeyCode}/${this.getAudioFileName(asset)}`;
    const result = await Filesystem.getUri({ path, directory: Directory.Data });
    return result.uri;
  }

  async getImageLocalPath(deliveryKeyCode, field): Promise<string> {
    const fileName = `${field}.jpg`;
    const uuid = localStorage.getItem('uuid');
    const path = `${uuid}/${deliveryKeyCode}/${fileName}`;
    const result = await Filesystem.getUri({ path, directory: Directory.Data });
    return result.uri;
  }

  async deleteFolder(deliveryKeyCode): Promise<any> {
    const uuid = localStorage.getItem('uuid');
    return Filesystem.rmdir({
      path: `${uuid}/${deliveryKeyCode}`,
      directory: Directory.Data,
      recursive: true,
    }).catch((err: any) => {
      this.toastService.showErrorInToast(serviceName, 'deleteFolder.removeRecursively', err);
    });
  }

  async clearAll(): Promise<any> {
    const uuid = localStorage.getItem('uuid');
    return Filesystem.rmdir({
      path: uuid,
      directory: Directory.Data,
      recursive: true,
    }).catch((err: any) => {
      this.toastService.showErrorInToast(serviceName, 'clearAll.removeRecursively', err);
    });
  }

  getBasePath(): Observable<any> {
    return of(Directory.Data);
  }

  async getCacheSize(): Promise<any> {
    const uuid = localStorage.getItem('uuid');
    let size = 0;

    try {
      const { files } = await Filesystem.readdir({ path: uuid, directory: Directory.Data });
      for (const entry of files) {
        try {
          const stat = await Filesystem.stat({ path: `${uuid}/${entry.name}`, directory: Directory.Data });
          size += stat.size || 0;
        } catch {}
      }
    } catch {}

    return size;
  }

  private getAudioFileName(params: any): string {
    return `${params.campaignTrackId}.mp3`;
  }

  private async ensureFolderDirectory(deliveryKeyCode: string): Promise<void> {
    const uuid = localStorage.getItem('uuid');
    await this.ensureDir(uuid);
    await this.ensureDir(`${uuid}/${deliveryKeyCode}`);
  }

  private async ensureDir(path: string): Promise<void> {
    try {
      await Filesystem.mkdir({ path, directory: Directory.Data, recursive: true });
    } catch {}
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
